// src/components/LandingPage.tsx
import React from "react";
import { motion } from "framer-motion";
import { LogIn, Loader2 } from "lucide-react";
import { OptimizedStarryBackground } from "./OptimizedStarryBackground";

interface LandingPageProps {
  handleLogin: () => Promise<void>;
  isLoading: boolean;
  activeAccount?: any;
}

const LandingPage: React.FC<LandingPageProps> = ({
  handleLogin,
  isLoading,
  activeAccount,
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex flex-col items-center justify-center relative">
      {/* Optimerad stjärnbakgrund */}
      <OptimizedStarryBackground />
      
      {/* Kort: logga + titlar + status + knappar */}
      <motion.div 
        className="relative z-10 bg-gray-800/80 rounded-2xl p-8 max-w-md w-full border border-gray-700 shadow-xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex flex-col items-center justify-center mb-6">
          {/* Använd Solkompaniets logga */}
          <motion.div 
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mb-4"
          >
            <img src="/solkompaniet_logo.png" alt="Solkompaniet" className="h-20 md:h-24" />
          </motion.div>

          <motion.h1 
            className="text-2xl md:text-3xl text-white font-bold text-center mt-2 mb-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Process Viewer
          </motion.h1>
          
          <motion.p 
            className="text-orange-300 text-sm md:text-base text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            Interaktiv processvisualisering
          </motion.p>
        </div>

        {/* Statusmeddelande */}
        <motion.div 
          className="bg-gray-900/50 p-4 rounded-lg mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          {isLoading ? (
            <p className="text-white flex items-center justify-center">
              <Loader2 className="animate-spin mr-2 h-5 w-5" />
              Kontrollerar inloggning...
            </p>
          ) : (
            <p className="text-gray-300 text-center">
              Du behöver logga in för att använda Process Viewer
            </p>
          )}
        </motion.div>

        {/* Inloggningsknapp */}
        <motion.div 
          className="flex flex-col space-y-3"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <button
            onClick={handleLogin}
            disabled={isLoading}
            className="relative flex items-center justify-center rounded-lg bg-orange-600 py-3 text-white font-medium 
                       hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin mr-2 h-5 w-5" />
                Loggar in...
              </>
            ) : (
              <>
                <LogIn className="mr-2 h-5 w-5" />
                Logga in med Microsoft-konto
              </>
            )}
          </button>
          
          <p className="text-xs text-gray-400 text-center mt-4">
            Vid problem med inloggning, kontakta IT-avdelningen
          </p>
        </motion.div>
      </motion.div>
      
      {/* Diskret sidfot med version */}
      <motion.div 
        className="absolute bottom-4 left-0 right-0 text-center text-xs text-gray-500"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
      >
        Solkompaniet Process Viewer v1.4.0
      </motion.div>
    </div>
  );
};

export default LandingPage;
